import { type CardMapType } from '@/defs/card'
import {
  GALLERY_GROUP_LABELS,
  GALLERY_GROUP_SLUG_UNGROUPED,
  GALLERY_GROUP_SLUGS_MV,
  GALLERY_GROUP_SLUGS_PRICE,
  GALLERY_GROUP_SLUGS_RARITY,
  GALLERY_GROUP_SLUGS_RELEASE_DATE,
  GALLERY_GROUP_SLUGS_TYPE,
  GALLERY_GROUP_SLUGS_TYPE_SIMPLE,
  type GalleryGroupbySlugType,
  type GalleryGroupSlugType,
  type GalleryGroupType,
} from '@/defs/gallery'
import { type PriceSourceType } from '@/defs/price'
import { REC_ROLE_DEFAULT, REC_ROLES, type RecRoleType, type RecType } from '@/defs/rec'
import { getUniqueRecKey } from '@/utils/recUtil'

import { getGalleryGroup } from './getGalleryGroup'

type GroupbyConfigType = Record<
  GalleryGroupbySlugType,
  {
    fn: (rec: RecType) => string | null
    slugs: string[]
  }
>

export type Props = {
  cardMap: CardMapType
  categories?: Omit<GalleryGroupType, 'recs'>[]
  groupby: GalleryGroupbySlugType
  isRoleFiltered?: boolean
  priceSource: PriceSourceType
  recs: RecType[]
  rolePref?: RecRoleType
}

type GroupReturnType = {
  groups: GalleryGroupType[]
  hasMultipleRoles: boolean
  role?: RecRoleType
  roles: RecRoleType[]
}

export const getGalleryGroups = ({
  cardMap,
  categories = [],
  groupby,
  isRoleFiltered,
  priceSource,
  recs,
  rolePref,
}: Props): GroupReturnType => {
  const categorySlugs = categories.map((c) => c.slug)

  const GROUPBY_CONFIG: GroupbyConfigType = {
    category: {
      fn: (rec) => rec.category ?? null,
      slugs: categorySlugs,
    },
    mv: {
      fn: (rec) => {
        const card = cardMap[rec.cardId]
        const cardPartner = rec.partnerRef ? cardMap[rec.partnerRef.cardId] : undefined

        if (card?.type?.includes('Land')) return 'mv-land'

        let mv = card?.cmc
        if (mv === undefined) return `mv-0`
        if (cardPartner?.isLegalCommander) mv += cardPartner.cmc || 0
        if (mv >= 15) return `mv-15+`
        return `mv-${mv}`
      },
      slugs: [...GALLERY_GROUP_SLUGS_MV],
    },
    price: {
      fn: (rec): GalleryGroupSlugType | null => {
        const card = cardMap[rec.cardId]

        const price = card?.priceSources?.[priceSource]?.price
        if (price === undefined) return null
        if (price < 0.5) return 'price-less-than-fifty-cents'
        if (price < 1) return 'price-fifty-cents-to-ninety-nine-cents'
        if (price < 2.5) return 'price-one-dollar-to-two-dollars-forty-nine'
        if (price < 5) return 'price-two-dollars-fifty-to-four-dollars-ninety-nine'
        if (price < 10) return 'price-five-dollars-to-nine-dollars-ninety-nine'
        if (price < 20) return 'price-ten-dollars-to-nineteen-dollars-ninety-nine'
        if (price < 50) return 'price-twenty-dollars-to-forty-nine-dollars-ninety-nine'
        if (price < 100) return 'price-fifty-dollars-to-ninety-nine-dollars-ninety-nine'
        return 'price-one-hundred-dollars-or-more'
      },
      slugs: [...GALLERY_GROUP_SLUGS_PRICE],
    },
    rarity: {
      fn: (rec) => `rarity-${cardMap[rec.cardId]?.rarity || 'other'}`,
      slugs: [...GALLERY_GROUP_SLUGS_RARITY],
    },
    releaseDate: {
      fn: (rec): GalleryGroupSlugType | null => {
        const releaseDate = cardMap[rec.cardId]?.releasedAt
        if (releaseDate === undefined) return null

        const releaseDateTime = new Date(releaseDate).getTime()

        if (releaseDateTime > new Date(new Date().setDate(new Date().getDate() - 90)).getTime())
          return 'date-last-3-months'

        if (releaseDateTime > new Date(new Date().setDate(new Date().getDate() - 180)).getTime())
          return 'date-last-6-months'

        if (releaseDateTime > new Date(new Date().setDate(new Date().getDate() - 365)).getTime())
          return 'date-last-year'

        if (releaseDateTime > new Date(new Date().setDate(new Date().getDate() - 730)).getTime())
          return 'date-last-2-years'

        if (releaseDateTime > new Date('2020-01-01').getTime()) return 'date-since-2020'

        if (releaseDateTime > new Date('2010-01-01').getTime()) return 'date-since-2010'

        if (releaseDateTime > new Date('2000-01-01').getTime()) return 'date-since-2000'

        return 'date-earliest'
      },
      slugs: [...GALLERY_GROUP_SLUGS_RELEASE_DATE],
    },
    type: {
      fn: (rec) => {
        if (rec.role === 'commander') return 'type-commander'
        return cardMap[rec.cardId]?.primaryType
          ? `type-${cardMap[rec.cardId]?.primaryType?.toLowerCase()}`
          : 'ungrouped'
      },
      slugs: [...GALLERY_GROUP_SLUGS_TYPE],
    },
    typeSimple: {
      fn: (rec) => (cardMap[rec.cardId]?.primaryType?.toLowerCase().includes('land') ? 'type-land' : 'type-spell'),
      slugs: [...GALLERY_GROUP_SLUGS_TYPE_SIMPLE],
    },
    ungrouped: {
      fn: (): GalleryGroupSlugType => GALLERY_GROUP_SLUG_UNGROUPED,
      slugs: [GALLERY_GROUP_SLUG_UNGROUPED],
    },
  } as const

  const groups: GalleryGroupType[] = GROUPBY_CONFIG[groupby].slugs.map((slug) => ({
    ...getGalleryGroup(slug, categories),
    recs: [],
  }))

  const groupUngrouped: GalleryGroupType = {
    ...GALLERY_GROUP_LABELS[GALLERY_GROUP_SLUG_UNGROUPED],
    recs: [],
    slug: GALLERY_GROUP_SLUG_UNGROUPED,
  }

  const slugGroupMap = new Map(groups.map((g) => [g.slug, g]))
  const slugSeenCardsInGroupMap = new Map<string, Set<string>>()
  const slugRolesInGroupMap = new Map<string, Set<RecRoleType>>()

  for (const rec of recs) {
    const groupSlug = GROUPBY_CONFIG[groupby].fn(rec)
    const group = (groupSlug ? slugGroupMap.get(groupSlug) : null) || groupUngrouped
    const recRole = rec.role || REC_ROLE_DEFAULT

    // NOTE: Tracks every role per group before role filtering is applied
    let rolesInGroupSet = slugRolesInGroupMap.get(group.slug)
    if (!rolesInGroupSet) {
      rolesInGroupSet = new Set()
      slugRolesInGroupMap.set(group.slug, rolesInGroupSet)
    }
    rolesInGroupSet.add(recRole)

    // NOTE: Removes duplicates of the same cardId + partnerRef + role combo
    const cardKey = getUniqueRecKey(rec)
    let seenCardsInGroupSet = slugSeenCardsInGroupMap.get(group.slug)
    if (!seenCardsInGroupSet) {
      seenCardsInGroupSet = new Set()
      slugSeenCardsInGroupMap.set(group.slug, seenCardsInGroupSet)
    }
    if (seenCardsInGroupSet.has(cardKey)) continue
    seenCardsInGroupSet.add(cardKey)

    group.recs.push(rec)
  }

  groups.push(groupUngrouped)

  const rolesSet = new Set([...slugRolesInGroupMap.values()].flatMap((s) => [...s]))
  const roles = REC_ROLES.filter((c) => rolesSet.has(c))

  // NOTE: When a single group has multiple roles (but NOT when theres multiple roles in the entire gallery)
  const hasMultipleRoles = [...slugRolesInGroupMap.values()].some((s) => s.size > 1)
  const role = rolePref && roles.includes(rolePref) ? rolePref : roles[0]

  const groupsRole =
    !isRoleFiltered || !hasMultipleRoles
      ? groups
      : groups.map((g) => ({ ...g, recs: g.recs.filter((r) => (r.role || REC_ROLE_DEFAULT) === role) }))

  return {
    groups: groupsRole.filter((g) => g.recs.length > 0),
    hasMultipleRoles,
    role,
    roles,
  }
}
